import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useHasPermission, useInfiniteHandler, useQueryToast } from 'hooks';
import i18next from 'i18next';
import {
  ICreateSaleChannel,
  ISalesChannelInfiniteResponse,
  ISalesChannelLightListParams,
  ISalesChannelSearchParams,
} from 'services/salesChannel/types';
import { ILightListItem, ISalesChannelAvailable } from 'services/types';
import { apiClient } from 'utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  IMusicPlatform,
  ISalesChannelResponse,
  IUpdateSaleChannel,
} from './types';

export const useGetSalesChannelsInfiniteList = (
  params: ISalesChannelSearchParams
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params.page,
    params.itemCount
  );

  return useInfiniteQuery<ISalesChannelResponse[], DefaultError>({
    queryKey: ['sales-channel-list', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'SalesChannel',
        params: { ...params, itemCount, page: pageParam },
      });

      const salesChannels =
        await responseHandler<ISalesChannelInfiniteResponse>(response, {
          groupName: `useGetSalesChannelsInfiniteList - ${pageParam}`,
        });

      return infiniteHandler(salesChannels);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetSalesChannelsLightList = (
  params?: ISalesChannelLightListParams
) => {
  const { distributionFormId, enabled = true } = params ?? {};
  const { responseHandler } = useResponseHandler();
  const hasPermission = useHasPermission();
  return useQuery<ILightListItem[], DefaultError>({
    queryKey: ['sales-chanel-light-list', distributionFormId],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `SalesChannel/LightList`,
        params: {
          ...(distributionFormId !== undefined && { distributionFormId }),
        },
      });

      return responseHandler(response, {
        groupName: 'useGetSalesChannelsLightList',
      });
    },
    enabled: enabled && hasPermission('SalesChannelPermissionsLIST**', 256),
  });
};

export const useGetSalesChannelsAvailable = () => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ISalesChannelAvailable, DefaultError>({
    queryKey: ['sales-chanel-available'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `SalesChannel/Available`,
      });

      return responseHandler(response, {
        groupName: 'useGetSalesChannelsAvailable',
      });
    },
  });
};

export const useGetSalesChannel = (id: string) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ISalesChannelResponse, DefaultError>({
    queryKey: ['sales-chanel', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `SalesChannel/${id}`,
      });

      return responseHandler(response, {
        groupName: 'useGetSalesChannel',
        notFoundPath: '/sales-channel',
      });
    },
  });
};

export const useDeleteSaleChannel = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useDeleteSaleChannel');

  return useMutation<boolean, DefaultError, string>({
    mutationFn: async (id: string) => {
      loading(
        i18next.t('service-message.remove.loading', {
          what: i18next.t('saleChannel.what'),
        })
      );

      const response = await apiClient({
        method: 'DELETE',
        url: 'SalesChannel',
        body: { id: id },
      });

      return responseHandler(response, {
        groupName: `useDeleteSaleChannel`,
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.remove.success', {
          what: i18next.t('saleChannel.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutSaleChannel = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePutSaleChannel');

  return useMutation<ISalesChannelResponse, DefaultError, IUpdateSaleChannel>({
    mutationFn: async body => {
      loading(
        i18next.t('create.update.loading', {
          what: body.name,
        })
      );

      const response = await apiClient({
        method: 'PUT',
        url: `SalesChannel`,
        body: body,
      });

      return responseHandler(response, {
        groupName: `usePutSaleChannel`,
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: data.name,
        })
      );
    },
    onError: dismiss,
  });
};

export const usePostSalesChannel = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePostSalesChannel');

  return useMutation<ISalesChannelResponse, DefaultError, ICreateSaleChannel>({
    mutationFn: async body => {
      loading(
        i18next.t('service-code.create.loading', {
          what: body.name,
        })
      );

      const response = await apiClient({
        method: 'POST',
        url: `SalesChannel`,
        body: body,
      });

      return responseHandler(response, {
        groupName: `usePostSalesChannel`,
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: data.name,
        })
      );
    },
    onError: dismiss,
  });
};

export const useGetSalesChannelProviders = (
  SalesChannelId: string | undefined,
  retry?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  const queryResult = useQuery<IMusicPlatform, DefaultError>({
    queryKey: ['sales-chanel-providers', SalesChannelId],
    enabled: !!SalesChannelId?.length,
    retry,
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `SalesChannel/Providers?SalesChannelId=${SalesChannelId}`,
      });

      return responseHandler(response, {
        groupName: 'useGetSalesChannelProviders',
        notFoundPath: '/sales-channel',
      });
    },
  });
  return queryResult;
};
